"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { VideoCard } from "./VideoCard";

// TikTok video IDs by language
const TIKTOK_VIDEOS = {
  default: "7584866112995724566", // Default video
  spanish: "7585235433173323030", // Spanish video
  french: "7585235433173323030", // French video
} as const;

// Additional videos for the carousel
const additionalVideos = [
  {
    id: 2,
    url: "https://www.tiktok.com/@chickytutor/video/7585235433173323030",
    videoId: "7585235433173323030",
  },
  {
    id: 3,
    url: "https://www.tiktok.com/@chickytutor/video/7584402221823954198",
    videoId: "7584402221823954198",
  },
];

interface SocialProofProps {
  selectedLanguageCode?: string | null;
}

export function SocialProof({ selectedLanguageCode }: SocialProofProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Determine which TikTok video to show based on selected language
  const mainVideoId = useMemo(() => {
    if (!selectedLanguageCode) return TIKTOK_VIDEOS.default;

    const code = selectedLanguageCode.toLowerCase();
    if (code.startsWith("es")) return TIKTOK_VIDEOS.spanish;
    if (code.startsWith("fr")) return TIKTOK_VIDEOS.french;

    return TIKTOK_VIDEOS.default;
  }, [selectedLanguageCode]);

  // Create array of all videos (main + additional)
  const allVideos = useMemo(() => {
    const filtered = additionalVideos.filter((v) => v.videoId !== mainVideoId);
    return [
      {
        id: 1,
        url: `https://www.tiktok.com/@chickytutor/video/${mainVideoId}`,
        videoId: mainVideoId,
      },
      ...filtered,
    ];
  }, [mainVideoId]);

  const totalSlides = allVideos.length;

  // Navigation functions - endless loop
  const goToPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
  };

  // Get indices for left, center, right phones (with wrapping)
  const getVisibleIndices = () => {
    const left = currentIndex === 0 ? totalSlides - 1 : currentIndex - 1;
    const center = currentIndex;
    const right = currentIndex === totalSlides - 1 ? 0 : currentIndex + 1;
    return { left, center, right };
  };

  const { left, center, right } = getVisibleIndices();

  return (
    <div className="relative w-full max-w-[500px] mx-auto">
      {/* Section Label */}
      <p className="text-center text-sm font-medium text-gray-500 mb-6 uppercase tracking-wider">
        👀 See it in action
      </p>

      {/* Stacked Phones Container */}
      <div className="relative h-[500px] sm:h-[580px] lg:h-[650px] flex items-center justify-center">
        {/* Left Phone - Behind, rotated left, more visible on big screens */}
        <div
          className="absolute -left-8 sm:-left-4 lg:-left-12 transition-all duration-500 ease-out cursor-pointer hover:scale-90"
          style={{
            transform:
              "translateX(0) rotate(-12deg) sm:rotate(-15deg) lg:rotate(-18deg) scale(0.75) sm:scale(0.7) lg:scale(0.65)",
            zIndex: 10,
            opacity: 0.8,
          }}
          onClick={goToPrev}
        >
          <VideoCard {...allVideos[left]} />
        </div>

        {/* Center Phone - Front and center */}
        <div
          className="relative transition-all duration-500 ease-out"
          style={{
            zIndex: 30,
            transform: "scale(1) sm:scale(1.05) lg:scale(1.1)",
          }}
        >
          <VideoCard {...allVideos[center]} />
        </div>

        {/* Right Phone - Behind, rotated right, more visible on big screens */}
        <div
          className="absolute -right-8 sm:-right-4 lg:-right-12 transition-all duration-500 ease-out cursor-pointer hover:scale-90"
          style={{
            transform:
              "translateX(0) rotate(12deg) sm:rotate(15deg) lg:rotate(18deg) scale(0.75) sm:scale(0.7) lg:scale(0.65)",
            zIndex: 10,
            opacity: 0.8,
          }}
          onClick={goToNext}
        >
          <VideoCard {...allVideos[right]} />
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrev}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-40 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all duration-200 hover:scale-110"
        aria-label="Previous video"
      >
        <ChevronLeft className="w-5 h-5 text-gray-700" />
      </button>

      <button
        onClick={goToNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-40 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all duration-200 hover:scale-110"
        aria-label="Next video"
      >
        <ChevronRight className="w-5 h-5 text-gray-700" />
      </button>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-4">
        {Array.from({ length: totalSlides }).map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-200 ${
              index === currentIndex ? "bg-orange-500 w-6" : "bg-gray-300 hover:bg-gray-400"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
